import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { uploadsUrl } from "../lib/uploader";
import { fmtDate } from "../lib/format";
import { RichTextRender } from "../components/RichTextRender";
import { Loading, ErrorBanner, Banner } from "../components/ui";

interface Perk {
  id: string;
  title: string;
  provider: string | null;
  body: string | null;
  thumbnailKey: string | null;
  linkUrl: string | null;
  publishedAt: string | null;
}

export function PerkDetailPage() {
  const { perkId } = useParams<{ perkId: string }>();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [data, setData] = useState<Perk | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!perkId) return;
    api
      .get<Perk>(`/perks/${perkId}${preview ? "?preview=1" : ""}`)
      .then(setData)
      .catch((e) => setErr((e as Error).message));
  }, [perkId, preview]);

  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!data) return <Loading />;

  const thumb = uploadsUrl(data.thumbnailKey);

  return (
    <article className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/perks"
        className="inline-block text-sm text-tora-ink/60 hover:text-tora"
      >
        ← 会員特典一覧へ
      </Link>

      {preview && (
        <Banner tone="warning">
          プレビュー表示中です（未公開／予約中の可能性があります）。会員にはまだ表示されていません。
        </Banner>
      )}

      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-tora/5 px-2 py-1 text-xs font-semibold text-tora-ink">
            <span aria-hidden="true">🎁</span>
            <span>会員特典</span>
          </span>
          {data.publishedAt && (
            <span className="text-xs text-tora-ink/60">
              {fmtDate(data.publishedAt)}
            </span>
          )}
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-tora leading-tight whitespace-pre-line">
          {data.title}
        </h2>
        {data.provider && (
          <p className="text-sm text-tora-ink/70">提供: {data.provider}</p>
        )}
      </header>

      {thumb && (
        <img
          src={thumb}
          alt=""
          className="w-full h-auto max-h-[70vh] object-contain rounded-xl border border-black/10 bg-white shadow-sm"
        />
      )}

      {data.body && <RichTextRender html={data.body} />}

      {data.linkUrl && (
        <a
          href={data.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary btn-lg w-full md:w-auto"
        >
          特典を利用する →
        </a>
      )}
    </article>
  );
}
